/**
 * useSocketEvent Hook
 *
 * Subscribe to Socket.io events with automatic cleanup.
 * Returns the latest event data.
 */

import { useEffect, useState, useRef } from 'react';
import { useSocketContext } from './SocketProvider';
import type {
  AssessmentCreatedEvent,
  TrainingAssignedEvent,
  SopProgressEvent,
  NotificationEvent,
} from './types';
import { SOCKET_EVENTS } from './types';

// Event data type mapping
type EventDataMap = {
  'assessment:created': AssessmentCreatedEvent;
  'assessment:updated': AssessmentCreatedEvent;
  'training:assigned': TrainingAssignedEvent;
  'training:completed': TrainingAssignedEvent;
  'sop:progress': SopProgressEvent;
  'sop:completed': SopProgressEvent;
  'sop:failed': SopProgressEvent;
  'notification:new': NotificationEvent;
  'notification:read': { id: string };
  'job:progress': { jobId: string; progress: number; status: string };
  'job:completed': { jobId: string };
  'job:failed': { jobId: string; error: string };
};

type KnownEventName = keyof EventDataMap;

/**
 * Listen to a single Socket.io event
 *
 * @param event - Event name to listen for
 * @param initialValue - Optional initial value
 * @returns Latest event data
 *
 * @example
 * const progress = useSocketEvent('sop:progress');
 * // progress is the latest SopProgressEvent or undefined
 */
export function useSocketEvent<E extends KnownEventName>(
  event: E,
  initialValue?: EventDataMap[E]
): EventDataMap[E] | undefined {
  const { socket } = useSocketContext();
  const [data, setData] = useState<EventDataMap[E] | undefined>(initialValue);

  useEffect(() => {
    if (!socket) return;

    const handler = (payload: EventDataMap[E]) => {
      setData(payload);
    };

    socket.on(event as string, handler);

    return () => {
      socket.off(event as string, handler);
    };
  }, [socket, event]);

  return data;
}

/**
 * Listen to a Socket.io event with a callback
 *
 * @param event - Event name to listen for
 * @param callback - Callback function called on each event
 *
 * @example
 * useSocketEventCallback('notification:new', (notification) => {
 *   showToast(notification.title);
 *   refetchNotifications();
 * });
 */
export function useSocketEventCallback<E extends KnownEventName>(
  event: E,
  callback: (data: EventDataMap[E]) => void
): void {
  const { socket } = useSocketContext();
  const callbackRef = useRef(callback);

  // Keep callback ref current
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!socket) return;

    const handler = (payload: EventDataMap[E]) => {
      callbackRef.current(payload);
    };

    socket.on(event as string, handler);

    return () => {
      socket.off(event as string, handler);
    };
  }, [socket, event]);
}

/**
 * Subscribe to SOP progress events for a specific SOP
 *
 * @param sopId - SOP ID to watch
 * @returns Latest progress data
 *
 * @example
 * const progress = useSopProgress('sop-123');
 * // Automatically subscribes/unsubscribes from SOP room
 */
export function useSopProgress(sopId: string | null) {
  const { subscribe, unsubscribe } = useSocketContext();
  const progress = useSocketEvent(SOCKET_EVENTS.SOP_PROGRESS);

  // Subscribe to SOP-specific room
  useEffect(() => {
    if (!sopId) return;

    subscribe('sop', sopId);

    return () => {
      unsubscribe('sop', sopId);
    };
  }, [sopId, subscribe, unsubscribe]);

  // Only return progress for the SOP we're watching
  if (!progress || progress.sopId !== sopId) {
    return null;
  }

  return progress;
}

/**
 * Hook that triggers a callback when any of the specified events occur
 *
 * @param events - Array of event names to listen for
 * @param callback - Callback triggered on any event (useful for refetching)
 *
 * @example
 * // Refetch assessments when new ones are created
 * useSocketRefetch(
 *   ['assessment:created', 'assessment:updated'],
 *   () => queryClient.invalidateQueries(['assessments'])
 * );
 */
export function useSocketRefetch(events: KnownEventName[], callback: () => void): void {
  const { socket } = useSocketContext();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!socket) return;

    const handler = () => {
      callbackRef.current();
    };

    // Subscribe to all events
    events.forEach((event) => {
      socket.on(event as string, handler);
    });

    return () => {
      events.forEach((event) => {
        socket.off(event as string, handler);
      });
    };
  }, [socket, events]);
}
